import * as api from '@gitbook/api-client';
import { SiteType } from '@gitbook/api-client';
import type { OrganizationBilling } from '@gitbook/app-database';
import { assertIsDefined } from '@gitbook/util-ts';

import { checkIsCommunityProductKey, checkIsNewProductKey } from './table_products';

export type NonLegacySiteType =
    | SiteType.Basic
    | SiteType.Premium
    | SiteType.Ultimate
    | SiteType.Sponsored;
export type LegacySiteType = SiteType.LegacyBasic | SiteType.LegacyPremium;
export type FreeSiteType = SiteType.Basic | SiteType.Sponsored;

export enum SitePlanTarget {
    CUSTOMERS = 'customers',
    OPEN_SOURCE = 'open-source',
}

/**
 * The pricing definition object for a site type
 */
export type SitePricingDefinition = {
    key: NonLegacySiteType;
    title: string;
    subtitle: string;
    description: string;
    targets: readonly SitePlanTarget[];
    featured?: boolean;
};

/**
 * An array of all Site products.
 *
 * The order matters: products should be sorted from lowest to highest.
 *
 * This is because we use indexes to determine whether we are upgrading or
 * downgrading, when switching from an product to another.
 */
export const SITE_PRICING_PRODUCTS: readonly SitePricingDefinition[] = [
    {
        key: SiteType.Basic,
        title: 'Basic',
        subtitle: 'Create a personal docs site for free',
        description: 'Public-only docs site with basic customization and features.',
        targets: [SitePlanTarget.OPEN_SOURCE, SitePlanTarget.CUSTOMERS],
    },
    {
        key: SiteType.Sponsored,
        title: 'Sponsored',
        subtitle: 'Incredible open source docs that fund your project',
        description: 'Public-only docs site to fund your open source project.',
        targets: [SitePlanTarget.OPEN_SOURCE],
    },
    {
        key: SiteType.Premium,
        title: 'Premium',
        subtitle: 'Build fully-branded, AI-powered documentation',
        description:
            'Docs site with custom domain, advanced customization and additional sharing options.',
        targets: [SitePlanTarget.CUSTOMERS],
    },
    {
        key: SiteType.Ultimate,
        title: 'Ultimate',
        subtitle: 'Centralize your docs with granular access control',
        description:
            'Docs site with sections, premium features, and full audience controls including authenticated access.',
        targets: [SitePlanTarget.CUSTOMERS],
    },
] as const;

/**
 * The pricing definition object for a Legacy Site type
 */
export type LegacySitePricingDefinition = {
    key: LegacySiteType;
    title: string;
    subtitle: string;
    description: string;
};

/**
 * An array of all Legacy Site products.
 *
 * The order matters: products should be sorted from lowest to highest.
 *
 * This is because we use indexes to determine whether we are upgrading or
 * downgrading, when switching from an product to another.
 */
export const LEGACY_SITE_PRICING_PRODUCTS: readonly LegacySitePricingDefinition[] = [
    {
        key: SiteType.LegacyBasic,
        title: 'Basic',
        subtitle: 'Unlimited public-only docs sites with basic customization and features.',
        description: 'Public-only docs site with basic customization and features.',
    },
    {
        key: SiteType.LegacyPremium,
        title: 'Pro',
        subtitle: 'Unlimited docs sites with all audience settings and all advanced features.',
        description:
            'Docs site with all audience settings including authenticated access, and all advanced features.',
    },
] as const;

export function isLegacySiteType(siteType: SiteType): siteType is LegacySiteType {
    return LEGACY_SITE_PRICING_PRODUCTS.some((product) => product.key === siteType);
}

export function isFreeSiteType(siteType: SiteType): siteType is FreeSiteType {
    return siteType === SiteType.Basic || siteType === SiteType.Sponsored;
}

/**
 * Asserts the given site type is non-legacy.
 */
export function assertNonLegacySiteType(siteType: SiteType): asserts siteType is NonLegacySiteType {
    if (isLegacySiteType(siteType)) {
        throw new Error(`Invalid non-legacy site type ${siteType}`);
    }
}

export function compareSiteTypes(a: SiteType, b: SiteType): number {
    const ALL_SITE_TYPES: SiteType[] = [
        ...LEGACY_SITE_PRICING_PRODUCTS.map((product) => product.key),
        ...SITE_PRICING_PRODUCTS.map((product) => product.key),
    ];

    return ALL_SITE_TYPES.indexOf(a) - ALL_SITE_TYPES.indexOf(b);
}

export function isSiteTypeDowngrade(from: SiteType, to: SiteType): boolean {
    return compareSiteTypes(from, to) > 0;
}

/**
 * Get the site product definition for a given site type.
 */
export function getSiteProductDefinition(
    siteType: SiteType
): SitePricingDefinition | LegacySitePricingDefinition {
    const products = isLegacySiteType(siteType)
        ? LEGACY_SITE_PRICING_PRODUCTS
        : SITE_PRICING_PRODUCTS;
    const definition = products.find((product) => product.key === siteType);
    assertIsDefined(definition, `Site type ${siteType} not found in pricing definitions`);
    return definition;
}

/**
 * Get the free site product relatively to the given organization billing record
 */
export function getFreeSiteProductForBilling(
    billing: OrganizationBilling | null
): SitePricingDefinition | LegacySitePricingDefinition {
    return (
        billing && checkIsNewProductKey(billing.product)
            ? SITE_PRICING_PRODUCTS
            : LEGACY_SITE_PRICING_PRODUCTS
    )[0];
}

/**
 * Assert that a pricing definition is for new pricing.
 */
export function assertIsNewSitePricingDefinition(
    value: SitePricingDefinition | LegacySitePricingDefinition
): asserts value is SitePricingDefinition {
    if (
        !(
            value.key === SiteType.Basic ||
            value.key === SiteType.Premium ||
            value.key === SiteType.Ultimate ||
            value.key === SiteType.Sponsored
        )
    ) {
        throw new Error(
            `Invalid or undefined pricing definition for a non-legacy site: ${JSON.stringify(value)}`
        );
    }
}

/**
 * Determines whether the given site type is paid.
 */
export function checkIsPaidSiteProductKey(
    key: string
): key is api.SiteType.Ultimate | api.SiteType.Premium {
    return key === api.SiteType.Premium || key === api.SiteType.Ultimate;
}

/**
 * Get the default site type for an organization based on its plan.
 * Community organizations get Sponsored sites, all others get Ultimate.
 */
export function getDefaultSiteTypeForOrg(organization: api.Organization): api.SiteType {
    return checkIsCommunityProductKey(organization.plan)
        ? api.SiteType.Sponsored
        : api.SiteType.Ultimate;
}
